<template>
  <b-overlay :show="loading" rounded="sm" spinner-type="grow" spinner-variant="warning">
    <b-jumbotron>
      <b-row>
        <b-col>
          <b-row align-h="start">
            <font-awesome-layers full-width class="fa-3x logo">
              <font-awesome-icon icon="square" style="color: #85458a" />
              <font-awesome-icon icon="code-branch" transform="shrink-7" style="color: white" />
            </font-awesome-layers>
            <h5>Branches</h5>
          </b-row>
        </b-col>
        <b-col offset="4" style="text-align: right">
          <b-button
            id="idNewBtn"
            size="sm"
            variant="outline-primary"
            v-b-modal="'idNewBranchModal'"
            @click="selectedBranch=null"
          >New</b-button>
        </b-col>
      </b-row>
      <b-row align-h="end">
        <b-col cols="3">
          <b-form-input
            v-model="filter"
            type="search"
            id="idFilterInput"
            placeholder="Type to Search"
            size="sm"
          ></b-form-input>
        </b-col>
      </b-row>
    </b-jumbotron>
    <div v-if="!showSpinner" style="margin: 0 1rem;">
      <AdminAreaNewBranchModal
        :branch="selectedBranch"
        @newBranch="handleNewBranch"
        @updateBranch="handleUpdateBranch"
      />
      <b-table
        id="idBranchesTable"
        head-variant="light"
        outlined
        per-page="20"
        responsive="sm"
        small
        show-empty
        hover
        selectable
        select-mode="single"
        :items="branches"
        :current-page="currentPage"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="filter"
        :filter-ignored-fields="ignoreFields"
        @filtered="onFiltered"
        @row-clicked="onBranchClicked"
      >
        <template v-slot:cell(actions)="row">
          <b-button-group>
            <b-button
              size="sm"
              @click="editBranchClicked(row.item)"
              variant="outline-primary"
              v-b-modal="'idNewBranchModal'"
            >
              <b-icon scale=".9" icon="pencil-square"></b-icon>Edit
            </b-button>
            <b-button size="sm" @click="deleteBranchClicked(row.item)" variant="outline-primary">
              <b-icon scale=".9" icon="trash"></b-icon>Delete
            </b-button>
          </b-button-group>
        </template>
      </b-table>
      <b-pagination
        style="margin-right: 1rem"
        v-model="currentPage"
        :total-rows="totalRows"
        first-number
        last-number
        per-page="20"
        align="right"
      />
    </div>
    <b-spinner
      variant="warning"
      class="m-5"
      style="width: 3rem; height: 3rem;"
      v-if="this.showSpinner"
    ></b-spinner>
  </b-overlay>
</template>

<script>
import AdminAreaNewBranchModal from "@/components/AdminAreaNewBranchModal.vue";
import moment from "moment";

export default {
  components: { AdminAreaNewBranchModal },
  data() {
    return {
      selectedBranch: null,
      fields: [
        { key: "branchId", sortable: true },
        { key: "branchName", sortable: true },
        { key: "createdBy", sortable: true },
        { key: "modificationDate", sortable: true },
        { key: "actions", sortable: false }
      ],
      branches: [],
      ignoreFields: ["id"],
      filter: null,
      loading: false,
      branchEntities: [],
      totalRows: 1,
      currentPage: 1,
      sortBy: "branchId",
      sortDesc: true,
      showSpinner: true
    };
  },
  async created() {
    try {
      await this.getBranches();
    } catch (error) {
      this.$root.$emit("showAlert", {
        type: "error",
        msg: `Error ${error.status} while retrieving Branches`
      });
    }
  },
  methods: {
    async getBranches() {
      this.showSpinner = true;
      this.branchEntities = await this.$buhApi.branchesGet();
      this.branches = this.branchEntities.map(branch => {
        return {
          id: branch.metadata.entityId,
          branchId: branch.externalReference,
          branchName: branch.name,
          createdBy: branch.metadata.createdByUser,
          modificationDate: moment(branch.metadata.dateTimeLastModified).format(
            "M/D/YYYY h:mm A"
          )
        };
      });
      this.totalRows = this.branches.length;
      this.showSpinner = false;
    },
    editBranchClicked(item) {
      this.selectedBranch = item;
    },
    async deleteBranchClicked(item) {
      const deleteConfirm = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the Branch: ${item.branchName}?`,
        {
          title: `Delete branch`,
          size: "md",
          buttonSize: "md",
          okVariant: "primary",
          okTitle: "Accept",
          cancelVariant: "outline-primary",
          cancelTitle: "Cancel",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true
        }
      );
      if (!deleteConfirm) return;
      this.loading = true;
      try {
        await this.$buhApi.branchesIdDelete(item.id);
      } catch (error) {
        this.loading = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status} while deleting Branch ${item.branchId}`
        });
      }
      this.branches = this.branches.filter(branch => branch.id !== item.id);
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Branch deleted"
      });
      this.loading = false;
    },
    async handleNewBranch(item) {
      this.loading = true;
      try {
        await this.$buhApi.branchesPost({
          name: item.branchName
        });
        await this.getBranches();
      } catch (error) {
        this.loading = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status} while creating ${item.branchName}`
        });
        // eslint-disable-next-line
        console.log(error);
        return;
      }
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Branch created"
      });
      this.loading = false;
    },
    async handleUpdateBranch(item) {
      this.loading = true;
      try {
        await this.$buhApi.branchesIdPut(item.id, {
          branchDto: {
            name: item.branchName
          }
        });
        await this.getBranches();
      } catch (error) {
        this.loading = false;
        this.$root.$emit("showAlert", {
          type: "error",
          msg: `Error ${error.status} while updating ${item.branchName}`
        });
        // eslint-disable-next-line
        console.log(error);
        return;
      }
      this.$root.$emit("showAlert", {
        type: "info",
        msg: "Branch updated"
      });
      this.loading = false;
    },
    onBranchClicked(item) {
      const branch = this.branchEntities.filter(
        branch => branch.metadata.entityId === item.id
      );
      this.$router.push({
        path: "adminAreaBranchDetail",
        query: { id: branch[0].metadata.entityId, name: branch[0].name }
      });
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  }
};
</script>